import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { isAuthenticated } from './utils/auth';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Paywall from './components/Paywall';
import QuickCalm from './components/QuickCalm';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './pages/NotFound';
import { DomainRedirect } from './utils/domain';
import routes from './routes';
import '@/App.css';

const PrivateRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/auth" />;
};

const PaywallRoute = ({ children, requireTier }) => {
  return isAuthenticated() ? (
    <Paywall requireTier={requireTier}>{children}</Paywall>
  ) : (
    <Navigate to="/auth" />
  );
};

const QuickCalmWrapper = () => {
  const location = useLocation();
  const publicRoutes = ['/', '/auth'];
  const quickCalmHidden = localStorage.getItem('neuroheal_quickcalm_hidden') === 'true';
  const showQuickCalm = isAuthenticated() && !publicRoutes.includes(location.pathname) && !quickCalmHidden;
  return showQuickCalm ? <QuickCalm /> : null;
};

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#050508]">
    <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const renderRoute = ([path, Component, guard, requireTier]) => {
  let element = <Component />;

  if (guard === 'paywall') {
    element = <PaywallRoute requireTier={requireTier}>{element}</PaywallRoute>;
  } else if (guard === 'private') {
    element = <PrivateRoute>{element}</PrivateRoute>;
  }

  return <Route key={path} path={path} element={element} />;
};

function App() {
  return (
    <LanguageProvider>
    <ThemeProvider>
    <SubscriptionProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <DomainRedirect />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {routes.map(renderRoute)}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <QuickCalmWrapper />
          <PWAInstallPrompt />
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </SubscriptionProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
